import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { ReactNode } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';
import { Employee } from 'src/features/employees/api/types/employee';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import EmployeesTableRow, {
  EmployeesActionsSlotPayload,
} from './employees-table-row';

interface Props {
  data: Employee[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  actionsSlot: (payload: EmployeesActionsSlotPayload) => ReactNode;
  isAllEmployeesFromCurrentPageSelected: boolean;
  toggleAllSelection: (isChecked: boolean, rows: string[]) => void;
  selectedEmployees: string[];
  toggleSelection: (isChecked: boolean, rowId: string) => void;
}

const EmployeesTable = ({
  isLoading,
  data,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  actionsSlot,
  isAllEmployeesFromCurrentPageSelected,
  toggleAllSelection,
  selectedEmployees,
  toggleSelection,
}: Props) => {
  const { t } = useTranslation();

  const { results } = useEmployeesGroupsByTokens(
    data
      ?.filter(employee => Boolean(employee.employeeGroupToken))
      .map(employee => employee.employeeGroupToken ?? ''),
  );

  const getEmployeeGroupName = (employeeGroupToken: string | undefined) => {
    const employeeGroup = results?.hits?.find(
      group => group.token === employeeGroupToken,
    );

    return employeeGroup?.name;
  };

  const columns = [
    {
      id: 'email',
      label: t('employees:employees.columns.employee'),
      isSortable: true,
    },
    {
      id: 'status',
      label: t('employees:employees.columns.invitationStatus'),
      isSortable: false,
    },
    {
      id: 'group',
      label: t('employees:employees.columns.group'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    data?.length > 0 && isAllEmployeesFromCurrentPageSelected
                  }
                  indeterminate={isAllEmployeesFromCurrentPageSelected}
                  onChange={e =>
                    toggleAllSelection(
                      e.target.checked,
                      data?.map(employee => employee.token),
                    )
                  }
                />
              </TableCell>

              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={true}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() => {
                        setSortController(prev => {
                          handleChangeSort([
                            `${column.id}:${
                              prev.sortOrder === SortOrder.ASC
                                ? SortOrder.DESC
                                : SortOrder.ASC
                            }`,
                          ]);
                          return {
                            ...prev,
                            sortOrder:
                              prev.sortOrder === SortOrder.ASC
                                ? SortOrder.DESC
                                : SortOrder.ASC,
                            sortBy: column.id,
                          };
                        });
                      }}
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}

              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(({ email, status, token, employeeGroupToken }) => {
              return (
                <EmployeesTableRow
                  key={token}
                  token={token}
                  email={email}
                  status={status}
                  employeeGroupToken={employeeGroupToken ?? ''}
                  groupName={getEmployeeGroupName(employeeGroupToken)}
                  actionsSlot={actionsSlot}
                  selectedEmployees={selectedEmployees}
                  toggleSelection={toggleSelection}
                />
              );
            })}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};

export default EmployeesTable;
